import type { QueryClient } from '@tanstack/react-query';

import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';

import type {
  ViewProjectResponseDto,
  GetScreenerResponseDto,
  ParticipantProjectsControllerGetScreenerData,
} from '@/api/core-api/types.gen';

import {
  participantProjectsControllerViewProject,
  participantProjectsControllerGetScreener,
} from '@/api/core-api';
import { queryKeys } from '@/hooks/api/query-keys';
import { utmSearchSchema } from '@/schemas/utm';
import { getClient } from '@/utils/get-api-client';

export interface GetProjectParams {
  projectId: string;
  userId?: string;
  query?: {
    referralCode?: string;
    inviteCode?: string;
    inviteId?: string;
  };
}

export type GetProjectResponse =
  | { success: true; data: ViewProjectResponseDto }
  | { success: false; error: string };

export const projectSearchSchema = utmSearchSchema.extend({
  referralCode: z.string().optional(),
  inviteCode: z.string().optional(),
  inviteId: z.string().optional(),
  invite: z.string().optional(),
  // `?cancel=true` deep-links from invitation emails (and legacy
  // /respondents/v2/surveys?id=…&cancel=true links redirected here by the
  // website) — opens the cancel-application prompt. See
  // cancel-application-prompt.tsx (ONCALL-2152). TanStack Router JSON-parses
  // search values, so `true` arrives as a boolean; `.catch` keeps malformed
  // values from crashing the page.
  cancel: z.boolean().optional().catch(undefined),
});

export type ProjectSearchParams = z.infer<typeof projectSearchSchema>;

export const fetchProject = async ({
  data,
}: {
  data: GetProjectParams;
}): Promise<ViewProjectResponseDto> => {
  const response = await participantProjectsControllerViewProject({
    client: getClient(data.userId),
    path: { id: data.projectId },
    query: data.query,
  });

  if (!response.data) {
    throw new Error('Failed to fetch project');
  }

  return response.data;
};

// Query options for React Query integration
export const projectQueryOptions = (
  projectId: string,
  search?: ProjectSearchParams,
  userId?: string,
) =>
  queryOptions({
    queryKey: queryKeys.projects.detail(projectId, search, userId),
    refetchOnWindowFocus: 'always',
    queryFn: () =>
      fetchProject({
        data: {
          projectId,
          query: {
            inviteCode: search?.inviteCode || search?.invite,
            referralCode: search?.referralCode,
            inviteId: search?.inviteId,
          },
          userId,
        },
      }),
  });

// Prefetch project data in route loader (for SSR)
export async function prefetchProjectData(
  queryClient: QueryClient,
  projectId: string,
  search: ProjectSearchParams,
  userId?: string,
): Promise<void> {
  void (await queryClient.ensureQueryData(
    projectQueryOptions(projectId, search, userId),
  ));
}

// Screener types
export type GetScreenerResponse =
  | { success: true; data: GetScreenerResponseDto }
  | { success: false; error: string };

// Screener fetch function
export const fetchScreener = async ({
  data,
}: {
  data: {
    projectId: string;
    userId?: string;
    query?: ParticipantProjectsControllerGetScreenerData['query'];
  };
}): Promise<GetScreenerResponse> => {
  try {
    const response = await participantProjectsControllerGetScreener({
      client: getClient(data.userId),
      path: { id: data.projectId },
      query: data.query,
    });

    if (response.error || !response.data) {
      return { success: false, error: 'Failed to fetch screener' };
    }

    return { success: true, data: response.data };
  } catch {
    return { success: false, error: 'Network error' };
  }
};

// Query options for screener (TanStack Query)
export const screenerQueryOptions = (
  projectId: string,
  userId?: string,
  query?: ParticipantProjectsControllerGetScreenerData['query'],
) =>
  queryOptions({
    queryKey: queryKeys.projects.screener(projectId, userId),
    queryFn: () => fetchScreener({ data: { projectId, userId, query } }),
  });

// Prefetch screener data in route loader (for SSR)
export async function prefetchScreenerData(
  queryClient: QueryClient,
  projectId: string,
  userId?: string,
  query?: ParticipantProjectsControllerGetScreenerData['query'],
): Promise<void> {
  await queryClient.ensureQueryData(
    screenerQueryOptions(projectId, userId, query),
  );
}
